/**
 * G4-T25.6 + G4-T25.8: Offline Sync Manager.
 *
 * API:
 *   syncManager.queueOperation(entityType, entityId, operation, payload)
 *   syncManager.flushQueue(authToken)
 *   syncManager.startAutoSync(getTokenFn)
 *   syncManager.stopAutoSync()
 *   syncManager.getPendingCount()
 *   syncManager.getLastSyncTime()
 */
import db from './syncDb';

// ── Device ID ──────────────────────────────────────────────────────────────

function getDeviceId() {
  let id = localStorage.getItem('ds_device_id');
  if (!id) {
    id = 'dev_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 9);
    localStorage.setItem('ds_device_id', id);
  }
  return id;
}

// ── UUID helper ────────────────────────────────────────────────────────────

function uuid() {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );
}

// ── Queue ──────────────────────────────────────────────────────────────────

/**
 * Bir değişikliği yerel kuyruğa ekler ve hemen göndermeye çalışır.
 * @param {string} entityType
 * @param {number} entityId
 * @param {string} operation  Create | Update | Delete
 * @param {object} payload    Seri hale getirilebiilr nesne
 * @param {string} [authToken] Varsa hemen gönder
 */
async function queueOperation(entityType, entityId, operation, payload, authToken) {
  const item = {
    localId:         uuid(),
    entityType,
    entityId,
    operation,
    payload:         typeof payload === 'string' ? payload : JSON.stringify(payload),
    clientTimestamp: new Date().toISOString(),
    deviceId:        getDeviceId(),
    status:          'pending',
    retries:         0,
  };

  await db.syncQueue.add(item);

  if (authToken && navigator.onLine) {
    await flushQueue(authToken);
  }
}

// ── Flush ──────────────────────────────────────────────────────────────────

const MAX_RETRIES = 3;
const BATCH_SIZE  = 50;

/**
 * Bekleyen tüm kayıtları sunucuya gönderir.
 * @param {string} authToken  Bearer token
 * @returns {{ processed: number, conflicts: number, failed: number }}
 */
async function flushQueue(authToken) {
  const pending = await db.syncQueue
    .where('status')
    .equals('pending')
    .limit(BATCH_SIZE)
    .toArray();

  if (pending.length === 0) return { processed: 0, conflicts: 0, failed: 0 };

  const items = pending.map(p => ({
    localId:         p.localId,
    entityType:      p.entityType,
    entityId:        p.entityId,
    operation:       p.operation,
    payload:         p.payload,
    clientTimestamp: p.clientTimestamp,
    deviceId:        p.deviceId,
  }));

  let response;
  try {
    const res = await fetch('/api/sync/push', {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${authToken}`,
      },
      body: JSON.stringify({ deviceId: getDeviceId(), items }),
    });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    response = await res.json();
  } catch (err) {
    // Ağ hatası — retry sayısını artır, max'a ulaşınca failed yap
    for (const p of pending) {
      const retries = (p.retries || 0) + 1;
      await db.syncQueue.update(p.id, {
        retries,
        status: retries >= MAX_RETRIES ? 'failed' : 'pending',
      });
    }
    console.warn('[SyncManager] flush failed:', err.message);
    return { processed: 0, conflicts: 0, failed: pending.length };
  }

  // Sonuçları işle
  const results  = response?.data?.results || [];
  let processed  = 0;
  let conflicts  = 0;

  for (const r of results) {
    const dbItem = pending.find(p => p.localId === r.localId);
    if (!dbItem) continue;

    if (r.status === 'Processed') {
      await db.syncQueue.update(dbItem.id, { status: 'sent' });
      processed++;
    } else if (r.status === 'Conflict') {
      await db.syncQueue.update(dbItem.id, { status: 'conflict' });
      conflicts++;
    } else {
      await db.syncQueue.update(dbItem.id, { status: 'failed' });
    }
  }

  // Timestamp güncelle
  await db.syncMeta.put({ key: 'lastPushAt', value: new Date().toISOString() });

  console.info(`[SyncManager] flush: processed=${processed} conflicts=${conflicts}`);
  return { processed, conflicts, failed: pending.length - processed - conflicts };
}

// ── Delta pull ─────────────────────────────────────────────────────────────

/**
 * Sunucudan son tarihten bu yana gelen değişiklikleri çeker.
 * @param {string} authToken
 * @param {string} [entityType]  Filtre (opsiyonel)
 * @returns {Array} delta değişiklikleri
 */
async function pullDelta(authToken, entityType) {
  const meta  = await db.syncMeta.get('lastPullAt');
  const since = meta?.value || new Date(0).toISOString();
  const deviceId = getDeviceId();

  const params = new URLSearchParams({ since, deviceId });
  if (entityType) params.append('entityType', entityType);

  try {
    const res = await fetch(`/api/sync/delta?${params}`, {
      headers: { 'Authorization': `Bearer ${authToken}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const body = await res.json();
    const changes = body?.data?.changes || [];

    await db.syncMeta.put({ key: 'lastPullAt', value: new Date().toISOString() });
    await applyDeltaToLocal(changes);
    return changes;
  } catch (err) {
    console.warn('[SyncManager] pullDelta failed:', err.message);
    return [];
  }
}

// ── Auto-sync ──────────────────────────────────────────────────────────────

let _getTokenFn   = null;
let _syncInterval = null;
let _onlineHandler = null;

const SYNC_INTERVAL_MS = 30_000; // 30 sn

/**
 * Otomatik sync'i başlatır.
 * - Online olunca kuyruğu gönderir (online event)
 * - Her SYNC_INTERVAL_MS'de bir flush çalıştırır
 * @param {() => string | null} getTokenFn  Güncel auth token dönen fonksiyon
 */
function startAutoSync(getTokenFn) {
  _getTokenFn = getTokenFn;

  _onlineHandler = async () => {
    const token = _getTokenFn?.();
    if (token) {
      console.info('[SyncManager] back online — flushing queue');
      await flushQueue(token);
    }
  };

  window.addEventListener('online', _onlineHandler);

  _syncInterval = setInterval(async () => {
    if (!navigator.onLine) return;
    const token = _getTokenFn?.();
    if (token) await flushQueue(token);
  }, SYNC_INTERVAL_MS);

  // İlk flush
  if (navigator.onLine) {
    const token = _getTokenFn?.();
    if (token) flushQueue(token);
  }
}

/** Otomatik sync'i durdurur. */
function stopAutoSync() {
  if (_onlineHandler) window.removeEventListener('online', _onlineHandler);
  if (_syncInterval)  clearInterval(_syncInterval);
  _onlineHandler = null;
  _syncInterval  = null;
}

// ── Entity cache refresh ───────────────────────────────────────────────────

/**
 * Sunucudan tüm BusinessRule'ları çekip IndexedDB'ye kaydeder.
 * @param {string} authToken
 * @returns {Array} kurallar
 */
async function refreshBusinessRules(authToken) {
  try {
    const res = await fetch('/api/rules', {
      headers: { 'Authorization': `Bearer ${authToken}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const body = await res.json();
    const rules = body?.data || body || [];
    await db.businessRules.clear();
    await db.businessRules.bulkPut(rules);
    await db.syncMeta.put({ key: 'businessRulesRefreshedAt', value: new Date().toISOString() });
    return rules;
  } catch (err) {
    console.warn('[SyncManager] refreshBusinessRules failed:', err.message);
    return db.businessRules.toArray();
  }
}

/**
 * Sunucudan tüm ProductGroup'ları çekip IndexedDB'ye kaydeder.
 * @param {string} authToken
 * @returns {Array} gruplar
 */
async function refreshProductGroups(authToken) {
  try {
    const res = await fetch('/api/productgroups', {
      headers: { 'Authorization': `Bearer ${authToken}` },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const body = await res.json();
    const groups = body?.data || body || [];
    await db.productGroups.clear();
    await db.productGroups.bulkPut(groups);
    await db.syncMeta.put({ key: 'productGroupsRefreshedAt', value: new Date().toISOString() });
    return groups;
  } catch (err) {
    console.warn('[SyncManager] refreshProductGroups failed:', err.message);
    return db.productGroups.toArray();
  }
}

/** IndexedDB'den BusinessRule listesini döner (offline kullanım). */
async function getLocalBusinessRules() {
  return db.businessRules.toArray();
}

/** IndexedDB'den ProductGroup listesini döner (offline kullanım). */
async function getLocalProductGroups() {
  return db.productGroups.toArray();
}

/**
 * Delta değişikliklerini yerel IndexedDB'ye uygular.
 * pullDelta sonucunda alınan changes listesi buraya verilir.
 * @param {Array} changes SyncDeltaItem[]
 */
async function applyDeltaToLocal(changes) {
  for (const change of changes) {
    const payload = change.payload ? JSON.parse(change.payload) : null;
    if (!payload) continue;

    if (change.entityType === 'BusinessRule') {
      if (change.operation === 'Delete') {
        await db.businessRules.delete(payload.id);
      } else {
        await db.businessRules.put(payload);
      }
    } else if (change.entityType === 'ProductGroup') {
      if (change.operation === 'Delete') {
        await db.productGroups.delete(payload.id);
      } else {
        await db.productGroups.put(payload);
      }
    }
  }
}

// ── Stats ──────────────────────────────────────────────────────────────────

/** Bekleyen kayıt sayısını döner. */
async function getPendingCount() {
  return db.syncQueue.where('status').equals('pending').count();
}

/** Son başarılı push zamanını döner (ISO string | null). */
async function getLastSyncTime() {
  const meta = await db.syncMeta.get('lastPushAt');
  return meta?.value || null;
}

const syncManager = {
  getDeviceId,
  queueOperation,
  flushQueue,
  pullDelta,
  startAutoSync,
  stopAutoSync,
  getPendingCount,
  getLastSyncTime,
  refreshBusinessRules,
  refreshProductGroups,
  getLocalBusinessRules,
  getLocalProductGroups,
  applyDeltaToLocal,
};

export default syncManager;
